import { useEffect, useMemo, useRef, useState } from 'react'

import { IntegrationServiceId } from '../../../../common/integrations'
import { useAppSelector } from '../../../../store/hooks'
import { selectCrossClientClients } from '../../../../store/crossClient/crossClientResults'
import { type CrossClientQueryResult } from '../../../../models/CrossClient'

import { useFetchMissingCrossClientClients } from '../../Results/hooks'

export interface WealthboxEligibleEntity {
  clientId: string
  externalId: string
}

export type WealthboxEligibility =
  | { status: 'loading' }
  | { status: 'error' }
  | {
      status: 'ready'
      totalCount: number
      eligibleCount: number
      skippedCount: number
      eligibleTargets: WealthboxEligibleEntity[]
    }

export const useWealthboxEligibility = (results: CrossClientQueryResult[]): WealthboxEligibility => {
  const clients = useAppSelector(selectCrossClientClients)
  const fetchMissingClients = useFetchMissingCrossClientClients()
  const fetchRef = useRef(fetchMissingClients)
  useEffect(() => {
    fetchRef.current = fetchMissingClients
  }, [fetchMissingClients])

  const [status, setStatus] = useState<'loading' | 'error' | 'ready'>(() => (results.length ? 'loading' : 'ready'))

  useEffect(() => {
    if (!results.length) {
      return
    }
    let cancelled = false

    fetchRef
      .current(results.map((result) => result.clientId))
      .then(() => {
        if (!cancelled) {
          setStatus('ready')
        }
      })
      .catch(() => {
        if (!cancelled) {
          setStatus('error')
        }
      })

    return () => {
      cancelled = true
    }
  }, [results])

  const eligibleTargets = useMemo<WealthboxEligibleEntity[]>(() => {
    if (status !== 'ready') {
      return []
    }
    return results.reduce<WealthboxEligibleEntity[]>((acc, result) => {
      const externalId = clients[result.clientId]?.integrations?.find((i) => i.serviceId === IntegrationServiceId.WEALTHBOX)?.externalId
      if (externalId) {
        acc.push({ clientId: result.clientId, externalId })
      }
      return acc
    }, [])
  }, [status, results, clients])

  if (status === 'loading') {
    return { status: 'loading' }
  }
  if (status === 'error') {
    return { status: 'error' }
  }

  const totalCount = results.length
  const eligibleCount = eligibleTargets.length
  return {
    status: 'ready',
    totalCount,
    eligibleCount,
    skippedCount: totalCount - eligibleCount,
    eligibleTargets,
  }
}
